import React, { useEffect, useState } from "react";
import { authStore } from "@/lib/authStore";
import {
  Database, Activity, AlertTriangle, CheckCircle2, RefreshCw, Loader2,
  TrendingUp, Building2, FileWarning,
} from "lucide-react";
import CorpusRetagPanel from "@/pages/admin/CorpusRetagPanel.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatTile({ icon: Icon, label, value, sub, tone = "default", pill = null }) {
  const toneCls =
    tone === "danger"  ? "border-red-500/40 bg-red-500/5"  :
    tone === "warn"    ? "border-amber-500/40 bg-amber-500/5" :
    tone === "good"    ? "border-emerald-500/40 bg-emerald-500/5" :
                         "border-border/50 bg-card";
  return (
    <div className={`rounded-2xl border ${toneCls} p-5`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        {pill}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function ScoreRing({ score }) {
  const tone = score >= 80 ? "text-emerald-500"
              : score >= 60 ? "text-amber-500"
              : "text-red-500";
  const verdict = score >= 80 ? "Healthy" : score >= 60 ? "Needs attention" : "Critical";
  return (
    <div className="flex items-center gap-4">
      <div className={`text-5xl font-semibold tabular-nums ${tone}`} data-testid="corpus-health-score">
        {score}<span className="text-2xl text-muted-foreground">/100</span>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <div className={`text-sm font-medium ${tone}`}>{verdict}</div>
          <StatusPill status={THRESHOLDS.score(score)} testid="status-pill-score" />
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">Composite corpus health</div>
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  const styles = {
    healthy: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    thin:    "bg-amber-500/10 text-amber-600 border-amber-500/30",
    minimal: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    absent:  "bg-red-500/10 text-red-600 border-red-500/30",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[verdict] || ""}`}>
      {verdict}
    </span>
  );
}

// V1.1 — health-status indicators (Healthy / Warning / Critical) for the
// 5 metrics specified by the user. Thresholds are CONSERVATIVE and intended
// to be re-calibrated after the 1-week baseline window completes.
function StatusPill({ status, testid }) {
  const map = {
    healthy:  { label: "Healthy",  cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" },
    warning:  { label: "Warning",  cls: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
    critical: { label: "Critical", cls: "bg-red-500/10 text-red-600 border-red-500/30" },
    unknown:  { label: "No data",  cls: "bg-muted/40 text-muted-foreground border-border/40" },
  };
  const m = map[status] || map.unknown;
  return (
    <span data-testid={testid} className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

// Thresholds — single source of truth. Adjust here once baseline data is in.
// `null`/`undefined` input → "unknown" pill (avoids false alarms before data).
const THRESHOLDS = {
  // Composite score 0–100 (higher is better)
  score:       (v) => v == null ? "unknown" : v >= 80 ? "healthy" : v >= 60 ? "warning" : "critical",
  // Total failed jobs (lower is better)
  failed_jobs: (v) => v == null ? "unknown" : v === 0 ? "healthy" : v <= 10 ? "warning" : "critical",
  // Untagged documents (lower is better)
  untagged:    (v) => v == null ? "unknown" : v === 0 ? "healthy" : v <= 20 ? "warning" : "critical",
  // Vendor match rate 0–100 % (higher is better) — only when vendor-named queries exist
  vendor_match:(v) => v == null ? "unknown" : v >= 75 ? "healthy" : v >= 50 ? "warning" : "critical",
  // Refusal rate 0–100 % (lower is better)
  refusal:     (v) => v == null ? "unknown" : v <= 15 ? "healthy" : v <= 35 ? "warning" : "critical",
};

export default function CorpusHealthTab() {
  const [data, setData]           = useState(null);
  const [untaggedCount, setUntaggedCount] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const token = authStore.getAccessToken();
      const [snapRes, untaggedRes] = await Promise.all([
        fetch(`${API}/admin/corpus-health`, {
          headers: { "Authorization": `Bearer ${token}` },
        }),
        fetch(`${API}/admin/corpus-health/untagged-docs?limit=500`, {
          headers: { "Authorization": `Bearer ${token}` },
        }),
      ]);
      if (!snapRes.ok) throw new Error(`HTTP ${snapRes.status}`);
      setData(await snapRes.json());
      if (untaggedRes.ok) {
        setUntaggedCount((await untaggedRes.json()).count ?? null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (loading && !data) return (
    <div className="flex items-center gap-2 text-muted-foreground" data-testid="corpus-health-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading corpus health…
    </div>
  );
  if (error) return (
    <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-600">
      Failed to load corpus health: {error}
    </div>
  );
  if (!data) return null;

  const score    = data.score?.score ?? 0;
  const comps    = data.score?.components ?? {};
  const meta     = data.metadata_health ?? {};
  const scorecard= data.vendor_scorecard ?? [];
  const failed   = data.failed_indexing_jobs ?? [];
  const backlog  = data.ingestion_backlog ?? {};
  const totalChunks = meta.total_chunks || 0;
  const taggedPct = meta.vendor_coverage_pct ?? 0;

  return (
    <div className="space-y-6" data-testid="corpus-health-tab">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            Corpus Health
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Operational governance signals for the RAG corpus — vendor coverage, metadata integrity, ingestion backlog.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-secondary text-sm"
          data-testid="corpus-health-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Score card */}
      <div className="rounded-2xl border border-border/50 bg-card p-6">
        <ScoreRing score={score} />
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(comps).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-muted/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {k.replace(/_/g, " ")}
              </div>
              <div className="text-lg font-semibold tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={Database} label="Total chunks" value={totalChunks.toLocaleString()} />
        <StatTile
          icon={CheckCircle2}
          label="Vendor metadata coverage"
          value={`${taggedPct}%`}
          sub={`${(totalChunks - meta.empty_vendor - meta.uuid_vendor).toLocaleString()} correctly tagged`}
          tone={taggedPct >= 90 ? "good" : taggedPct >= 70 ? "warn" : "danger"}
        />
        <StatTile
          icon={FileWarning}
          label="Untagged documents"
          value={untaggedCount ?? "—"}
          sub="documents that need vendor re-tagging"
          tone={(untaggedCount || 0) === 0 ? "good" : (untaggedCount || 0) <= 20 ? "warn" : "danger"}
          pill={<StatusPill status={THRESHOLDS.untagged(untaggedCount)} testid="status-pill-untagged" />}
        />
        <StatTile
          icon={Activity}
          label="Ingestion backlog (24 h)"
          value={backlog.recent_pending_24h || 0}
          sub={
            backlog.stale_pending
              ? `${backlog.stale_pending} stale (>24 h)`
              : "no stale jobs"
          }
          tone={(backlog.recent_pending_24h || 0) > 20 ? "warn" : "default"}
        />
      </div>

      {/* Vendor scorecard */}
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" /> Vendor Scorecard
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-scorecard-table">
            <thead className="text-xs text-muted-foreground border-b border-border/50">
              <tr>
                <th className="text-left py-2 px-2 font-medium">Vendor</th>
                <th className="text-right px-2 font-medium">Docs total</th>
                <th className="text-right px-2 font-medium">Indexed</th>
                <th className="text-right px-2 font-medium">Failed</th>
                <th className="text-right px-2 font-medium">Chunks (tagged)</th>
                <th className="text-right px-2 font-medium">Body-mention</th>
                <th className="text-center px-2 font-medium">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {scorecard.map(v => (
                <tr key={v.vendor} className="border-b border-border/30 last:border-0" data-testid={`vendor-row-${v.vendor.toLowerCase()}`}>
                  <td className="py-2.5 px-2 font-medium">{v.vendor}</td>
                  <td className="text-right px-2 tabular-nums">{v.documents_total}</td>
                  <td className="text-right px-2 tabular-nums">{v.documents_indexed}</td>
                  <td className={`text-right px-2 tabular-nums ${v.documents_failed > 0 ? "text-red-600" : ""}`}>
                    {v.documents_failed}
                  </td>
                  <td className="text-right px-2 tabular-nums">{v.chunks_correctly_tagged.toLocaleString()}</td>
                  <td className="text-right px-2 tabular-nums text-muted-foreground">{v.chunks_with_body_mention.toLocaleString()}</td>
                  <td className="text-center px-2"><VerdictBadge verdict={v.verdict} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Failed indexing jobs */}
      {failed.length > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Failed Indexing Jobs ({failed.length})
            <StatusPill status={THRESHOLDS.failed_jobs(failed.length)} testid="status-pill-failed-jobs" />
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {failed.slice(0, 25).map((j, idx) => (
              <div key={(j.id || j.doc_id || idx) + "-" + idx} className="text-sm bg-card rounded-lg px-3 py-2 border border-border/40">
                <div className="font-medium">{j.filename || j.blob_path || "(no filename)"}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{j.error || "(no error)"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* P2-B.1 production-window telemetry */}
      {data.retrieval_telemetry_7d && data.retrieval_telemetry_7d.total_queries > 0 && (
        <div className="rounded-2xl border border-border/50 bg-card p-5" data-testid="retrieval-telemetry-panel">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Retrieval Telemetry — last {data.retrieval_telemetry_7d.days} days
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl bg-muted/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Queries</div>
              <div className="text-lg font-semibold tabular-nums">{data.retrieval_telemetry_7d.total_queries}</div>
            </div>
            <div className="rounded-xl bg-muted/30 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Refusal rate</div>
                <StatusPill
                  status={THRESHOLDS.refusal(data.retrieval_telemetry_7d.refusal_rate_pct)}
                  testid="status-pill-refusal"
                />
              </div>
              <div className="text-lg font-semibold tabular-nums">{data.retrieval_telemetry_7d.refusal_rate_pct}%</div>
            </div>
            <div className="rounded-xl bg-muted/30 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Vendor-match (when named)</div>
                <StatusPill
                  status={THRESHOLDS.vendor_match(data.retrieval_telemetry_7d.vendor_match?.avg_rate)}
                  testid="status-pill-vendor-match"
                />
              </div>
              <div className="text-lg font-semibold tabular-nums">{data.retrieval_telemetry_7d.vendor_match?.avg_rate ?? "—"}%</div>
            </div>
            <div className="rounded-xl bg-muted/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Retrieval latency avg / p95</div>
              <div className="text-lg font-semibold tabular-nums">
                {data.retrieval_telemetry_7d.retrieval_latency?.avg_ms ?? "—"} / {data.retrieval_telemetry_7d.retrieval_latency?.p95_ms ?? "—"} ms
              </div>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Thresholds are conservative defaults — will be recalibrated after the 1-week observation baseline.
          </div>
        </div>
      )}

      {/* Chunks by vendor — full distribution */}
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-primary" /> Chunks by vendor (top 20)
        </h3>
        <div className="space-y-1.5">
          {(data.chunks_by_vendor || []).slice(0, 20).map(row => {
            const isLeak = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(row.vendor);
            const pct = (row.chunks / Math.max(totalChunks, 1)) * 100;
            return (
              <div key={row.vendor} className="flex items-center gap-3 text-sm">
                <div className={`w-48 truncate ${isLeak ? "text-red-600 font-mono text-xs" : ""}`}>
                  {isLeak ? `⚠️ UUID-leak (${row.vendor.slice(0, 12)}…)` : row.vendor}
                </div>
                <div className="flex-1 bg-muted/30 rounded-full h-2 overflow-hidden">
                  <div className={`h-full ${isLeak ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <div className="text-right tabular-nums w-20 text-muted-foreground">
                  {row.chunks.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Corpus Debt — admin re-tag + dedupe (D1/D2/D3) */}
      <CorpusRetagPanel />
    </div>
  );
}
